import { NgStyle } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
} from '@angular/core';
import { TRANSLATION_CATEGORIES } from '@entities/constants';
import { MessageToastConfig, TOAST_KEYS } from '@shared/models';
import { ToastService } from '@shared/services';
import { MasterApiCreateMessage } from '@shared/typescript';
import { ExtendedString, TimerUtility, timeoutGuard } from '@shared/utils';
import { ToastModule } from 'primeng/toast';
import { ButtonComponent } from '@shared/components/button/button.component';
import { TimeFormatPipe } from '@shared/pipes';

@Component({
  selector: 'app-confirmation-toast',
  imports: [ToastModule, NgStyle, ButtonComponent, TimeFormatPipe],
  templateUrl: './confirmation-toast.component.html',
  styleUrl: './confirmation-toast.component.scss',
})
export class ConfirmationToastComponent {
  @Input() position: MessageToastConfig['position'] = 'top-center';
  @Input() key: MessageToastConfig['key'] = TOAST_KEYS.confirm;

  /** Key will help you to identify the backend value */
  @Input() translationCategory = TRANSLATION_CATEGORIES.PAGE_TEXT;
  @Input() labelInfo: MessageToastConfig['labels'];
  @Input() styleClassName = '';

  @Output() onReject = new EventEmitter<TOAST_CONFIRMATION>();
  @Output() onConfirm = new EventEmitter<TOAST_CONFIRMATION>();

  timer?: TimerUtility;
  timeRemaining = 0;
  _autoCloseInfo: MessageToastConfig['autoClose'];

  isPopupVisible = false;

  constructor(private toastService: ToastService) {}

  @Input() set autoCloseInfo(value: typeof this._autoCloseInfo) {
    this._autoCloseInfo = value;
    this.handleTimer();
  }

  @HostListener('document:keydown.enter', ['$event'])
  handleEnterKey(event: KeyboardEvent) {
    if (this.isPopupVisible || this._autoCloseInfo) {
      event.preventDefault();
      const currentToast = this.toastService.$toastChange.value;
      if (currentToast?.summary) {
        this.confirmationHandler(currentToast.summary, {
          successMessage: [],
          errorMessage: [],
        });
      }
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleEscapeKey(event: KeyboardEvent) {
    if (this.isPopupVisible) {
      event.preventDefault();
      const currentToast = this.toastService.$toastChange.value;
      if (currentToast?.summary) {
        this.rejectionHandler(currentToast.summary);
      }
    }
  }

  ngOnInit(): void {
    this.registerToastAction();
  }

  handleTimer() {
    this.timeRemaining = this._autoCloseInfo?.duration ?? 0;
    // Clear previous timer if exists
    if (this.timer) {
      this.timer.stop();
      this.timer = undefined;
    }

    const timeout = this._autoCloseInfo?.duration;
    if (timeout) {
      this.timer = new TimerUtility({
        onComplete: () => {
          this.timerCleanup();
          this.rejectionHandler(TOAST_CONFIRMATION.AUTO_REJECT, true);
        },
        onTimeRunning: (time) => {
          this.timeRemaining = time;
        },
      });
      this.timer.start(timeout);
    }
  }

  confirmationHandler(summary: string, messageDetail: MasterApiCreateMessage) {
    if (messageDetail.successMessage?.length > 0)
      this.rejectionHandler(TOAST_CONFIRMATION.OPERATION_SUCCESS);
    else this.onConfirm.emit(this.compareMsg(summary));
    this.toastService.$isToastVisible.next(false);
    this.timerCleanup();
  }
  rejectionHandler(
    summary: ExtendedString<TOAST_CONFIRMATION>,
    isDefaultSummary = false,
  ) {
    this.onReject.emit(
      isDefaultSummary
        ? (summary as TOAST_CONFIRMATION)
        : this.compareMsg(summary),
    );
    this.toastService.$isToastVisible.next(false);
    // this.isPopupVisible = false
    this.timerCleanup();
  }

  getIconClass(summary: string, severity: string) {
    if (
      this.compareMsg(summary) === TOAST_CONFIRMATION.DELETE &&
      severity !== 'info'
    )
      return 'pi pi-times-circle';
    else return 'pi pi-info-circle';
  }

  compareMsg(mssg: string) {
    const message = mssg.toLocaleLowerCase();
    if (message.includes('copy')) return TOAST_CONFIRMATION.COPY;
    else if (message.includes('delete')) return TOAST_CONFIRMATION.DELETE;
    else if (message.includes('edit')) return TOAST_CONFIRMATION.EDIT;
    else if (message.includes('send')) return TOAST_CONFIRMATION.SEND;
    else return TOAST_CONFIRMATION.DELETE;
  }

  registerToastAction() {
    this.toastService.$isToastVisible.subscribe((flag) => {
      if (this._autoCloseInfo) {
        timeoutGuard(() => {
          this.isPopupVisible = flag;
          this.timerCleanup();
        });
        return;
      }

      this.isPopupVisible = flag;
    });
  }

  timerCleanup() {
    if (this.timer) {
      this.timer.stop();
      this.timer = undefined;
    }
  }

  ngOnDestroy() {
    this.timer?.stop();
  }
}

export enum TOAST_CONFIRMATION {
  DELETE = 'Delete',
  COPY = 'Copy',
  EDIT = 'Edit',
  SEND = 'Send',
  AUTO_REJECT = 'Auto-rejected',
  OPERATION_SUCCESS = 'Operation_Success',
}
